import {
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { DeductionExt, DeductionListJson, api_fetch } from "@/api"
import { UserState } from "@/auth/user.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { TableControls } from "@/deduction_table/table_controls.tsx"
import { INITIAL_TABLE_STATE, TableState } from "@/deduction_table/table_state.tsx"
import { capitalCase } from "change-case"
import { PageControls } from "@/deduction_table/page_controls.tsx"
import { handleDeductionTableSearch } from "./page.tsx"
import { dateFilter, defaultFilter, fuzzyFilter } from "@/utils/filters.ts"
import { DeductionNavbar, GlobalFilters } from "@/app/navbar"
import { BASE_COLUMNS_WITHOUT_BACKUP_PARSE, backupParseColumn } from "@/deduction_table/columns.tsx"
import { createOrgSpecificColumns } from "@/deduction_table/org_columns.tsx"
import { GlobalFilterState } from "@/global_filter"
import { OrgConfigState, loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { map, toISODateString, useAsyncEffect, usePageTitle } from "@/utils"
import { useMemo, useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { openDeductionDetailModal, DeductionDetailModalId } from "./deduction_detail_modal_state"
import { DeductionDetailModal } from "@/deduction_detail/DeductionDetailModal.tsx"
import { filter_won_pending_payment_rows } from "@/deduction/won_pending_payment"

type FilterKind = "won-pending-payment"

const FILTER_CONFIG: Record<FilterKind, { title: string; filter: (data: DeductionListJson[]) => DeductionListJson[] }> = {
  "won-pending-payment": {
    title: "Won — Pending Payment",
    filter: filter_won_pending_payment_rows,
  },
}

interface FilteredDeductionListProps {
  filterKind: FilterKind
}

export function FilteredDeductionListPage({ filterKind }: FilteredDeductionListProps) {
  const config = FILTER_CONFIG[filterKind]
  usePageTitle(config.title)
  const [, navigate] = useLocation()
  let user = UserState.use(u => u!)
  let tableState = TableState.use(ts => ts!)
  let [loading, setLoading] = useState(true)
  let [serverData, setServerData] = useState<DeductionListJson[]>([])
  let orgConfig = OrgConfigState.use()
  let modalDeductionId = DeductionDetailModalId.use((id: string | null) => id)

  const { startDate, endDate, distributor, dateKind } = GlobalFilterState.use(gf => gf!)

  useAsyncEffect(async () => {
    setServerData([])
    setLoading(true)
    await loadOrgFieldConfigs()
    const res = await api_fetch<DeductionListJson[]>("/v2/deduction/list", {
      method: "POST",
      body: {
        start_date: map(startDate, toISODateString),
        end_date: map(endDate, toISODateString),
        distributor,
        date_kind: dateKind,
      },
    })
    setLoading(false)
    if (!res.ok) {
      throw new Error("Failed to fetch data")
    }
    setServerData(res.value.data)
    TableState.set(s => {
      let pagination = s.pagination
      pagination.pageIndex = 0
      return { ...s, pagination }
    })
  }, [startDate, endDate, distributor, dateKind])

  let columns = useMemo(() => {
    const orgColumns = createOrgSpecificColumns(user.org_id!, orgConfig.orgFieldConfigs)
    return [...BASE_COLUMNS_WITHOUT_BACKUP_PARSE, ...orgColumns, backupParseColumn]
  }, [user.org_id, orgConfig.orgFieldConfigs])

  let data = useMemo(() => config.filter(serverData), [serverData, filterKind])

  const table = useMemo(() => {
    return useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      autoResetPageIndex: false,
      getColumnCanGlobalFilter: c => c.columnDef.enableGlobalFilter ?? true,
      globalFilterFn: fuzzyFilter,
      filterFns: {
        dateFilter: dateFilter,
        default: defaultFilter,
      },
      onColumnFiltersChange: function (updater: any) {
        TableState.set(s => {
          let columnFilters = updater(s.columnFilters)
          let pagination = s.pagination
          pagination.pageIndex = 0
          return { ...s, columnFilters, pagination }
        })
      },
      onColumnVisibilityChange: function (updater: any) {
        const visibility = updater(tableState.columnVisibility!) as VisibilityState
        TableState.set({ ...tableState, columnVisibility: visibility })
      },
      onGlobalFilterChange: handleDeductionTableSearch,
      onRowSelectionChange: function (updater: any) {
        const selection = updater(tableState.rowSelection!) as RowSelectionState
        TableState.set({ ...tableState, rowSelection: selection })
      },
      onSortingChange: function (updater: any) {
        let new_sort = updater(tableState.sorting!) as SortingState
        new_sort.splice(0, 0, { id: "validated" } as ColumnSort)
        TableState.set(s => ({
          ...s,
          sorting: new_sort,
        }))
      },
      onPaginationChange: function (updater: any) {
        let new_pagination = updater(tableState.pagination!) as PaginationState
        TableState.set({ ...tableState, pagination: new_pagination })
      },
      state: {
        columnVisibility: tableState.columnVisibility,
        rowSelection: tableState.rowSelection as RowSelectionState,
        sorting: tableState.sorting,
        globalFilter: tableState.search,
        pagination: tableState.pagination,
        columnFilters: tableState.columnFilters,
      },
      meta: {
        resetRowSelection: () => {
          TableState.set(s => ({ ...s, rowSelection: {} }))
        },
        clearAllFilters: () => {
          TableState.set(current => ({
            ...INITIAL_TABLE_STATE,
            columnVisibility: current.columnVisibility,
            sorting: current.sorting,
          }))
        },
      },
    })
  }, [data, columns, tableState])

  function handleRowClick(row: Row<DeductionExt>, event: MouseEvent) {
    let ded = row.original
    if (event.metaKey) {
      window.open(`/deduction/${ded.id}`, "_blank")
    } else {
      openDeductionDetailModal(ded.id)
    }
  }

  let rows = table.getRowModel().rows
  return (
    <div>
      <DeductionDetailModal />
      <div class="px-4">
        <DeductionNavbar>
          <GlobalFilters />
        </DeductionNavbar>
      </div>
      <div class="px-4 mt-6 mb-4">
        <div class="flex items-center gap-3">
          <Button variant="outline" class="h-8 w-8 p-0" onClick={() => navigate("/deductions")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 class="text-lg font-semibold">{config.title}</h1>
        </div>
      </div>
      <div class="mx-auto mb-16">
        <TableControls table={table} />
        <div class="border w-full">
          <div class="overflow-auto w-full">
            <table class="w-full border-collapse" style={{ tableLayout: "auto" }}>
              <thead class="bg-slate-50">
                {table.getHeaderGroups().map((headerGroup, idx) => (
                  <tr key={`${headerGroup.id}-${idx}`} class="border-b">
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={`${header.id}-${headerIdx}`}
                        class="px-1 py-1 text-left text-xs font-medium text-slate-700 whitespace-nowrap"
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#f8fafc",
                          zIndex: 10,
                          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                          maxWidth: "180px",
                          width: "auto",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={header.column.id ? capitalCase(header.column.id) : ""}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    {Array.from({ length: columns.length }).map((_, cellIndex) => (
                      <td key={cellIndex} class="px-4 py-3 border-b">
                        <Skeleton className="w-[100px] h-[20px] rounded-full bg-slate-500" />
                      </td>
                    ))}
                  </tr>
                ) : rows.length ? (
                  rows.map((row, idx) => {
                    let isModalOpen = modalDeductionId === row.original.id
                    return (
                      <tr
                        key={`${row.id}-${idx}`}
                        class={`hover:bg-slate-50 ${isModalOpen ? "bg-blue-100" : ""}`}
                        onClick={event => handleRowClick(row as any, event)}>
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            class="px-1 py-1 border-b whitespace-nowrap text-xs"
                            style={{
                              maxWidth: "180px",
                              width: "auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={String(cell.getValue() || "")}
                            key={`${cell.id}-${cellIdx}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} class="h-24 text-center">
                      No results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div class="flex items-center justify-between p-2 px-4">
          <PageControls table={table} />
        </div>
      </div>
    </div>
  )
}

export function WonPendingPaymentPage() {
  return <FilteredDeductionListPage filterKind="won-pending-payment" />
}
